import React, { useEffect, useMemo } from 'react';
import { Modal } from '@aibuild/ui';
import type { ProcessNode } from './graphTypes';
import type { RunInputValue } from '@api/processRuns';
import RunConfigForm, {
  type RunConfigFormValue,
} from './run/RunConfigForm';
import { liveInputNodeToDto } from './run/liveInputNodeToDto';
import { Body } from './InputValueDialog.styled';

export interface InputValueDialogProps {
  open: boolean;
  /**
   * The live Input node the user is running. The dialog uses this to
   * derive the title (the user-defined `node.data.label`) and to feed
   * {@link RunConfigForm} so the right input UI (text field for TEXT /
   * NUMBER, disabled hint for file types) is rendered.
   */
  node: ProcessNode | null;
  /** Last backend MISSING_REQUIRED_INPUT (or similar) error to surface inline. */
  inputError?: { readonly message: string; readonly missingInputs: readonly string[] } | null;
  isStarting: boolean;
  onClose: () => void;
  /** Optional clear hook so reopening the dialog resets the backend error. */
  onClearError?: () => void;
  /**
   * Called with the {@link RunInputValue} the user produced for this
   * Input node. The dialog only ever produces the {@code text} variant —
   * file-typed inputs are explicitly rejected by the single-node run
   * endpoint (they require the two-phase {@code prepare/start} flow), so
   * the form disables the file drop-zone here and the user is pointed at
   * the full Test Run dialog for those.
   */
  onSubmit: (value: RunInputValue) => Promise<void>;
  dataTestId?: string;
}

/**
 * Modal that prompts the user for a value when running a single Input
 * node from the editor (e.g. "Run from here" on an Input card).
 *
 * <p>This is the per-node sibling of {@link TestRunDialog}: both wrap the
 * same {@link RunConfigForm}. The single-node {@code POST /nodes/:id/run}
 * endpoint cannot accept file uploads (those need the two-phase
 * {@code /runs:prepare} + {@code /runs:start} flow which mints presigned
 * PUT URLs scoped to the run's S3 prefix), so this dialog passes
 * {@code fileUploadEnabled=false} to the form. File-typed Input nodes
 * therefore render a hint pointing the user at the full Test Run dialog
 * — text-typed inputs continue to work inline.</p>
 */
const InputValueDialog: React.FC<InputValueDialogProps> = ({
  open,
  node,
  inputError,
  isStarting,
  onClose,
  onClearError,
  onSubmit,
  dataTestId = 'input-value-dialog',
}) => {
  const inputNodeDtos = useMemo(
    () => (node ? [liveInputNodeToDto(node)] : []),
    [node],
  );

  useEffect(() => {
    if (open) onClearError?.();
  }, [open, onClearError]);

  const handleSubmit = (values: Record<string, RunConfigFormValue>) => {
    if (!node) return;
    // The form keys by output port id; for a single Input node row the
    // map has exactly one entry, so just pluck the first value.
    const formValue = Object.values(values)[0];
    if (!formValue) return;
    if (formValue.kind !== 'text') {
      // Defensive: file uploads are disabled in the form via
      // fileUploadEnabled=false, so we never expect to see one. Fail
      // loudly (and abort the submit) if a future change misconfigures
      // the form -- a silent return would leave the user staring at a
      // dead "Run" button with no clue what went wrong.
      console.error(
        '[InputValueDialog] Unexpected non-text value kind; file uploads must use the two-phase Test Run flow.',
        { nodeId: node.id, kind: formValue.kind },
      );
      return;
    }
    void onSubmit({ text: formValue.text });
  };

  if (!open || !node) return null;

  const nodeLabel = node.data.label || 'Input';

  return (
    <Modal
      open
      onClose={isStarting ? () => undefined : onClose}
      title={`Run "${nodeLabel}"`}
      subtitle="Provide a value to feed downstream nodes for this test run."
      size="sm"
      dataTestId={dataTestId}
      hideCloseButton={isStarting}
      disableEscape={isStarting}
      disableBackdropClose={isStarting}
    >
      <Body data-testid={`${dataTestId}__body`}>
        <RunConfigForm
          inputNodes={inputNodeDtos}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isStarting}
          inputError={inputError ?? null}
          submitLabel="Run"
          fileUploadEnabled={false}
          dataTestId={`${dataTestId}__form`}
        />
      </Body>
    </Modal>
  );
};

export default InputValueDialog;
